import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { MessageCircle, User, Clock, BookOpen } from 'lucide-react';
import { trackTrialCtaClick } from '../lib/analytics';

const M_COPY = {
  es: { subtitle: 'Cada clase está diseñada específicamente para ti', cta: 'Reservar mi clase de prueba gratis' },
  de: { subtitle: 'Jede Stunde wird speziell für dich vorbereitet', cta: 'Starte deinen Spanischunterricht' },
  en: { subtitle: 'Every lesson is designed specifically for you', cta: 'Start learning Spanish' },
};

export const Methodology = () => {
  const { t, language } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const icons = [MessageCircle, User, Clock, BookOpen];
  const colors = [
    { bg: 'from-[#A8D8EA] to-[#7DBDD9]', light: 'bg-[#E8F5FA]', border: 'border-[#A8D8EA]' },
    { bg: 'from-[#F5A6A4] to-[#E38987]', light: 'bg-[#FFF0EF]', border: 'border-[#F5A6A4]' },
    { bg: 'from-[#7DBDD9] to-[#A8D8EA]', light: 'bg-[#C8E7F2]', border: 'border-[#7DBDD9]' },
    { bg: 'from-[#E38987] to-[#F5A6A4]', light: 'bg-[#FFE5E4]', border: 'border-[#E38987]' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.215, 0.61, 0.355, 1]
      }
    }
  };

  return (
    <section id="methodology" className="py-24 bg-gradient-to-b from-[#E8F5FA] to-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#A8D8EA] rounded-full mix-blend-multiply filter blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#F5A6A4] rounded-full mix-blend-multiply filter blur-3xl" />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">{t.methodology.subtitle}</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mt-4 mb-6">
            {t.methodology.title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mx-auto" />
        </motion.div>

        {/* Methodology Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {t.methodology.cards.map((card, index) => {
            const Icon = icons[index];
            const color = colors[index];
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ y: -10, scale: 1.03 }}
                className="relative group"
              >
                {/* Card Number */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-[#4A4A4A] to-[#2A2A2A] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                  {index + 1}
                </div>

                {/* Card Content */}
                <div className={`relative h-full p-8 ${color.light} rounded-3xl border-2 ${color.border} shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden`}>
                  {/* Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-br ${color.bg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-[#4A4A4A] mb-4">
                    {card.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed">
                    {card.description}
                  </p>

                  {/* Decorative Element */}
                  <div className={`absolute -bottom-8 -right-8 w-24 h-24 bg-gradient-to-br ${color.bg} rounded-full opacity-10 group-hover:scale-150 transition-transform duration-500`} />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-lg text-gray-600 mb-6">{M_COPY[language]?.subtitle || M_COPY.es.subtitle}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => { trackTrialCtaClick('methodology'); document.getElementById('booking').scrollIntoView({ behavior: 'smooth' }); }}
            className="px-8 py-4 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            {M_COPY[language]?.cta || M_COPY.es.cta}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};